import React from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, NavLink, Link } from "react-router-dom";
import { useScrollRestoration } from "./hooks/useScrollRestoration";
import styles from "./App.module.css";
import navStyles from "./styles/Navigation.module.css";
import HomePage from "./pages/HomePage";
import StartHere from "./pages/learn/StartHere";
import IntervalsLearn from "./pages/learn/IntervalsLearn";
import ChordsLearn from "./pages/learn/ChordsLearn";
import ProgressionsLearn from "./pages/learn/ProgressionsLearn";
import MelodiesLearn from "./pages/learn/MelodiesLearn";
import IntervalQuizWrapper from "./pages/drill/intervals/IntervalQuizWrapper";
import ChordQuizWrapper from "./pages/drill/chords/ChordQuizWrapper";
import ChordProgressionWrapper from "./pages/drill/progressions/ChordProgressionWrapper";
import MelodyQuizWrapper from "./pages/drill/melodies/MelodyQuizWrapper";
import SoundLibraryDemo from "./pages/demos/SoundLibraryDemo";
import FeedbackPage from "./pages/FeedbackPage";
import MobileMenu from "./components/MobileMenu";
import useTheme from "./hooks/useTheme";
import ThemeToggle from "./components/ThemeToggle";
import AudiatedLogo from "./components/AudiatedLogo";
import { ROUTES, LEARN_NAV_ITEMS, DRILL_NAV_ITEMS, DEMO_NAV_ITEMS } from "./config/routes";
import DropdownNav from "./components/DropdownNav";

function ScrollRestoration() {
    useScrollRestoration();
    return null;
}

function Layout() {
    const { theme, toggleTheme } = useTheme();

    return (
        <div className={styles.app}>
            <header className={navStyles.appHeader}>
                <nav>
                    <Link to={ROUTES.HOME} className={navStyles.navBrand}>
                        <AudiatedLogo variant="header" />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className={navStyles.navLinks}>
                        <div className={navStyles.navGroup}>
                            <NavLink
                                className={({ isActive }) =>
                                    `${navStyles.routeLink} ${isActive ? navStyles.active : ""}`
                                }
                                to={ROUTES.HOME}
                                end
                            >
                                Home
                            </NavLink>
														<DropdownNav label="Learn" items={LEARN_NAV_ITEMS} />
                        </div>

                        <div className={navStyles.navGroup}>
                            {/* Drill navigation - individual links shown on desktop (>1064px) */}
                            <div className={navStyles.desktopOnly}>
                                {DRILL_NAV_ITEMS.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        className={({ isActive }) =>
                                            `${navStyles.routeLink} ${isActive ? navStyles.active : ""}`
                                        }
                                        to={item.to}
                                    >
                                        {item.label}
                                    </NavLink>
                                ))}
                            </div>

                            {/* Drill navigation - dropdown shown on medium screens (768px-1064px) */}
                            <div className={navStyles.mediumScreenOnly}>
                                <DropdownNav label="Drills" items={DRILL_NAV_ITEMS} />
                            </div>
                        </div>

                        <div className={navStyles.navGroup}>
                            {DEMO_NAV_ITEMS.map((item) => (
                                <NavLink
                                    key={item.to}
                                    className={({ isActive }) =>
                                        `${navStyles.routeLink} ${isActive ? navStyles.active : ""}`
                                    }
                                    to={item.to}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            {/* Temporarily removed for initial launch */}
                            {/* <NavLink
                                className={({ isActive }) =>
                                    `${navStyles.routeLink} ${isActive ? navStyles.active : ""}`
                                }
                                to={ROUTES.SPOTIFY}
                            >
                                Spotify
                            </NavLink> */}
                        </div>
                    </div>

                    {/* Mobile Navigation */}
                    <MobileMenu
                        learnItems={LEARN_NAV_ITEMS}
                        drillItems={DRILL_NAV_ITEMS}
                        demoItems={DEMO_NAV_ITEMS}
                        homeRoute={ROUTES.HOME}
                    />

                    <ThemeToggle theme={theme} toggle={toggleTheme} />
                </nav>
            </header>
            <div className={styles.pageContent}>
                <Outlet />
            </div>
        </div>
    );
}

// Learn Pages

// Drill Pages
function IntervalsDrill() {
    return <IntervalQuizWrapper />;
}

function ChordsDrill() {
    return <ChordQuizWrapper />;
}

function ProgressionsDrill() {
    return <ChordProgressionWrapper />;
}

function MelodieDrill() {
    return <MelodyQuizWrapper />;
}

// Demo Pages
function SoundDemo() {
    return <SoundLibraryDemo />;
}

export default function App() {
    return (
        <Router>
            <ScrollRestoration />
            <Routes>
                <Route path="/" element={<Layout />}>
                    <Route index element={<HomePage />} />

                    {/* Learn Routes */}
                    <Route path="Learn">
                        <Route index element={<StartHere />} />
                        <Route path="Intervals" element={<IntervalsLearn />} />
                        <Route path="Chords" element={<ChordsLearn />} />
                        <Route path="Progressions" element={<ProgressionsLearn />} />
                        <Route path="Melodies" element={<MelodiesLearn />} />
                    </Route>

                    {/* Drill Routes */}
                    <Route path="Drill">
                        <Route path="Intervals" element={<IntervalsDrill />} />
                        <Route path="Chords" element={<ChordsDrill />} />
                        <Route path="Progressions" element={<ProgressionsDrill />} />
                        <Route path="Melodies" element={<MelodieDrill />} />
                    </Route>

                    {/* Demo Routes */}
                    <Route path="Demos">
                        <Route path="Sound" element={<SoundDemo />} />
                        {/* Temporarily removed for initial launch */}
                        {/* <Route path="Theory" element={<TheoryDemo />} /> */}
                    </Route>

                    {/* Temporarily removed for initial launch */}
                    {/* Spotify Route */}
                    {/* <Route path="Spotify" element={<SpotifyPage />} /> */}

                    {/* Feedback Route - Outside the main layout since it has its own header */}
                    <Route path="feedback" element={<FeedbackPage />} />
                </Route>
            </Routes>
        </Router>
    );
}
